// ─── localStorage Keys ───────────────────────────────────────────
export const STORAGE_KEYS = {
  TOKEN: 'agentick_token',
  TENANT: 'agentick_tenant',
  USER: 'agentick_user',
  SETUP_BANNER_DISMISSED: 'agentick_setup_banner_dismissed',
} as const;

// ─── Timing (ms) ─────────────────────────────────────────────────
export const TIMING = {
  COOLDOWN_TICK_MS: 1_000,
  WHATSAPP_POLL_MS: 3_000,
  WHATSAPP_QR_POLL_MS: 20_000,
  ONBOARDING_POLL_MS: 3_000,
  PAYMENT_POLL_MS: 2_000,
  PAYMENT_MAX_POLL_MS: 5 * 60 * 1_000,
  API_TIMEOUT_MS: 30_000,
  WS_RECONNECT_DELAY_MS: 5_000,
  QUERY_STALE_MS: 5 * 60 * 1_000,
  QUERY_GC_MS: 30 * 60 * 1_000,
  CONFETTI_DURATION_MS: 4_000,
  TOOLTIP_RESET_MS: 1_500,
  SUGGESTION_BLUR_MS: 200,
  COPIED_RESET_MS: 2_000,
  CAMPAIGN_REDIRECT_MS: 500,
  ONBOARDING_ADVANCE_MS: 1_500,
} as const;

// ─── Limits ──────────────────────────────────────────────────────
export const LIMITS = {
  MAX_WS_MESSAGES_PER_CONVERSATION: 500,
  MAX_WS_SEEN_IDS: 5_000,
  MAX_API_KEYS: 10,
  MAX_TEMPLATE_BUTTONS: 3,
  MAX_TEMPLATE_BODY_LENGTH: 4_096,
  MAX_TEMPLATE_BUTTON_TEXT: 60,
  OTP_LENGTH: 6,
  OTP_COOLDOWN_SECONDS: 60,
  MAX_RECIPIENTS_DISPLAY: 100,
  MAX_TAGS_SHOWN: 3,
  MAX_CAMPAIGN_NAME_LENGTH: 255,
  DEFAULT_AB_SPLIT: 50,
  DEFAULT_THROTTLE_PER_SECOND: 10,
  MAX_PROFILE_IMAGE_BYTES: 5 * 1024 * 1024,
  MIN_PHONE_LENGTH: 5,
} as const;

// ─── Toast ───────────────────────────────────────────────────────
export const TOAST_LIMIT = 1;
export const TOAST_REMOVE_DELAY = 5_000;

// ─── Enums / Domain Values ───────────────────────────────────────
export const TONES = ['professional', 'friendly', 'casual', 'empathetic'] as const;
export type Tone = (typeof TONES)[number];

export const ROLES = { ADMIN: 'ADMIN', AGENT: 'AGENT' } as const;

export const WHATSAPP_STATUS = {
  WORKING: 'WORKING',
  CREATED: 'CREATED',
  SCAN_QR: 'SCAN_QR',
  FAILED: 'FAILED',
  DISCONNECTED: 'DISCONNECTED',
} as const;

export const TEMPLATE_STATUS = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
} as const;

export const CAMPAIGN_STATUS = {
  DRAFT: 'DRAFT',
  SCHEDULED: 'SCHEDULED',
  SENDING: 'SENDING',
  SENT: 'SENT',
  PAUSED: 'PAUSED',
  CANCELLED: 'CANCELLED',
} as const;

export const RECIPIENT_STATUS = ['ALL', 'PENDING', 'SENT', 'DELIVERED', 'READ', 'REPLIED', 'FAILED'] as const;

export const CAMPAIGN_TYPE = {
  BROADCAST: 'BROADCAST',
  AB_TEST: 'AB_TEST',
  DRIP: 'DRIP',
} as const;

export const PAYMENT_STATUS = {
  CREATED: 'CREATED',
  PAID: 'PAID',
  FAILED: 'FAILED',
  EXPIRED: 'EXPIRED',
} as const;

export const PAYMENT_TYPE = {
  SUBSCRIPTION: 'SUBSCRIPTION',
  TOPUP: 'TOPUP',
} as const;

export const CONVERSATION_MODE = {
  AI: 'AI',
  HUMAN: 'HUMAN',
} as const;

export const API_KEY_STATUS = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
} as const;

export const TEMPLATE_CATEGORY = {
  MARKETING: 'MARKETING',
  UTILITY: 'UTILITY',
  AUTHENTICATION: 'AUTHENTICATION',
} as const;

export const MESSAGE_ROLE = {
  CUSTOMER: 'customer',
  AI: 'ai',
  AGENT: 'agent',
} as const;

// ─── Reusable UI Arrays ─────────────────────────────────────────
export const TAG_SUGGESTIONS = ['vip', 'repeat-buyer', 'new-lead', 'premium', 'inactive', 'promo-subscriber'] as const;

export const API_KEY_SCOPES = [
  'messages:send',
  'conversations:read',
  'conversations:write',
  'campaigns:read',
  'campaigns:write',
  'contacts:read',
  'contacts:write',
  'templates:read',
  'templates:write',
  'analytics:read',
] as const;

export const KNOWLEDGE_MIME_MAP: Record<string, string> = {
  'application/pdf': '📄',
  'text/plain': '📝',
  'text/markdown': '📑',
};

export const KNOWLEDGE_ACCEPTED_TYPES = ['text/plain', 'text/markdown', 'application/pdf'] as const;

export const COLOR_PRESETS = [
  '#10b981', '#0d9488', '#06b6d4', '#3b82f6', '#6366f1',
  '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308',
  '#84cc16', '#78716c',
] as const;

export const DEFAULT_TAG_COLOR = '#10b981';

// ─── Defaults (Agent Config) ─────────────────────────────────────
export const DEFAULT_AGENT_ROLE = 'Customer Support Agent';
export const DEFAULT_GREETING = 'Hi there! How can I help you today?';
export const DEFAULT_HANDOFF = 'Let me connect you with a team member who can help further.';

// ─── Date Formats ────────────────────────────────────────────────
export const DATE_FORMAT = {
  DISPLAY: 'MMM D, YYYY',
  API: 'YYYY-MM-DD',
  SHORT: 'MMM D',
  FULL_DAY: 'dddd, MMM D',
  DATETIME: 'MMM D, YYYY h:mm A',
  TIME: 'h:mm A',
  TIME_PRECISE: 'h:mm:ss A',
  SHORT_DATETIME: 'MMM D, h:mm A',
} as const;

// ─── Chart Colors ────────────────────────────────────────────────
export const CHART_COLORS = {
  EMERALD: '#10b981',
  BLUE: '#3b82f6',
  AMBER: '#f59e0b',
  VIOLET: '#8b5cf6',
  ORANGE: '#f97316',
  GRID: '#27272a',
  TICK: '#71717a',
  LEGEND: '#a1a1aa',
} as const;

export const CONFETTI_COLORS = ['#10b981', '#14b8a6', '#06b6d4', '#3b82f6', '#8b5cf6', '#f59e0b', '#ec4899', '#f43f5e'] as const;

// ─── WhatsApp Preview Colors ─────────────────────────────────────
export const WHATSAPP_COLORS = {
  BG: '#0b141a',
  BUBBLE_SELF: '#005c4b',
  BUBBLE_OTHER: '#202C33',
  BUTTON_TEXT: '#53bdeb',
} as const;

export const APP_BG = '#09090b';

// ─── Playground Suggestions ──────────────────────────────────────
export const PLAYGROUND_SUGGESTIONS = [
  'What are your business hours?',
  'I want to return a product',
  'Tell me about your services',
  'I need to speak to a human',
] as const;

// ─── Pagination ──────────────────────────────────────────────────
export const DEFAULT_PAGE_SIZE = 20;
